import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Search,
  Plus,
  Trash2,
  Eye,
  Upload,
  Filter,
  Users,
  Phone,
  Mail,
  CreditCard,
  Calendar,
  MoreVertical,
  CheckCircle,
  XCircle,
  X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsersPage, deleteUser, createUser, removeUserFromState } from '../redux/slices/userSlice';
import { PERMISSIONS, usePermissionHook, PERMISSION_DENIED_MESSAGE, permissionGuardClass } from '../utils/permissions';
import toast from 'react-hot-toast';
import { useSocket } from '../context/SocketContext';
import { ModalReporter } from '../context/ModalContext';
import { Dropdown } from '../components/ui/dropdown-01';
import { NumberedPagination } from '../components/ui/numbered-pagination';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 350;

// Yerel tarihe göre YYYY-MM-DD (backend dateFrom/dateTo formatı)
const toApiDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Tarih filtresi seçeneğini backend'in beklediği dateFrom/dateTo aralığına çevir (dateTo dahil)
const DATE_FILTER_DAYS = { today: 0, week: 7, month: 30, year: 365 };
const getDateFilterRange = (dateFilter) => {
  const days = DATE_FILTER_DAYS[dateFilter];
  if (days === undefined) return {};
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - days);
  return { dateFrom: toApiDate(from), dateTo: toApiDate(to) };
};

const UserList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state — yalnızca ekrandaki sayfa tutulur, toplam backend'den gelir
  const {
    rows: users,
    total: totalUsers,
    totalPages: serverTotalPages,
    loading,
    loaded,
    error,
  } = useSelector((state) => state.users.userList);

  // Socket handler'ları güncel sayfa/arama ile yeniden çekebilsin diye
  const reloadPageRef = useRef(() => {});

  // WebSocket Entegrasyonu
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;

    // Backend: user-created
    const handleUserCreated = (data) => {
      const userName = data.firstName && data.lastName 
        ? `${data.firstName} ${data.lastName}` 
        : data.tcNo || 'Yeni kullanıcı';
      toast.success(`✅ Yeni Kullanıcı Oluşturuldu: ${userName}`, {
        duration: 3000,
      });

      // Ekrandaki sayfayı yenile (toplam ve sıralama backend'den gelir)
      reloadPageRef.current();
    };

    // Backend: user-deleted
    const handleUserDeleted = (data) => {
      const deletedTcNo = data?.tcNo || data?.tc || data?.identityNum;
      
      if (!deletedTcNo) {
        return;
      }
      
      // Kullanıcıyı state'den kaldır, ardından sayfayı backend'den tazele
      // Bu, hem kendi silme işlemimiz hem de başka yerden yapılan silme işlemleri için çalışır
      dispatch(removeUserFromState(deletedTcNo));
      reloadPageRef.current();
    };

    // Backend event'lerini dinle
    socket.on('user-created', handleUserCreated);
    socket.on('user-deleted', handleUserDeleted);

    // Eski event isimlerini de dinle (geriye dönük uyumluluk için)
    socket.on('user:new', handleUserCreated);

    return () => {
      socket.off('user-created', handleUserCreated);
      socket.off('user-deleted', handleUserDeleted);
      socket.off('user:new', handleUserCreated);
    };
  }, [socket, dispatch]);

  // Local state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [dateFilter, setDateFilter] = useState('today');
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, user: null });
  const [isDeleting, setIsDeleting] = useState(false);

  // Herhangi bir modal açıkken arka plan (body) scroll'unu kilitle — sağ scrollbar
  // şeridi ve arka plan kayması nedeniyle oluşan beyaz alanları engeller.
  const isAnyModalOpen = showAddModal || deleteConfirm.show;
  useEffect(() => {
    if (!isAnyModalOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isAnyModalOpen]);
  const [currentPage, setCurrentPage] = useState(1);
  const canManageCustomers = usePermissionHook(PERMISSIONS.USERS_MANAGE);

  // Form state
  const [formData, setFormData] = useState({
    tcNo: '',
    gsm: '',
    password: '',
    firstName: '',
    lastName: '',
    email: '',
  });

  // Arama: yazma bitince (debounce) backend'e gönder ve 1. sayfaya dön
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchTerm.trim();
      if (next === debouncedSearch) return;
      setDebouncedSearch(next);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearch]);

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    setCurrentPage(1);
  };

  // Arama varken tarih filtresi yok sayılır: arama tüm müşterilerde yapılır
  const isSearching = debouncedSearch.length > 0;
  const dateRange = useMemo(
    () => (isSearching ? {} : getDateFilterRange(dateFilter)),
    [isSearching, dateFilter]
  );
  const hasActiveFilters = isSearching || dateFilter !== 'all';

  // Ekrandaki sayfayı backend'den iste
  const loadPage = useCallback(() => {
    dispatch(fetchUsersPage({
      page: currentPage,
      limit: PAGE_SIZE,
      search: debouncedSearch,
      dateFrom: dateRange.dateFrom,
      dateTo: dateRange.dateTo,
    }));
  }, [dispatch, currentPage, debouncedSearch, dateRange]);

  useEffect(() => {
    reloadPageRef.current = loadPage;
  }, [loadPage]);

  // Sayfa, arama veya tarih değiştiğinde yeniden çek
  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const totalPages = Math.max(1, serverTotalPages || 1);

  // Silme sonrası son sayfa boşaldıysa var olan son sayfaya çek
  useEffect(() => {
    if (!loading && loaded && !error && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [loading, loaded, error, currentPage, totalPages]);

  // Kullanıcı oluşturma fonksiyonu
  const handleCreateUser = async () => {
    if (!canManageCustomers) {
      toast.error(PERMISSION_DENIED_MESSAGE);
      return;
    }
    try {
      // Validation
      if (!formData.tcNo || formData.tcNo.length !== 11) {
        toast.error('TC No 11 haneli olmalıdır');
        return;
      }
      if (!formData.gsm || formData.gsm.length < 10) {
        toast.error('Telefon numarası en az 10 haneli olmalıdır');
        return;
      }
      if (!formData.password || formData.password.length < 6) {
        toast.error('Şifre en az 6 karakter olmalıdır');
        return;
      }

      await dispatch(createUser(formData)).unwrap();

      toast.success('Kullanıcı başarıyla oluşturuldu');

      // Formu temizle
      setFormData({
        tcNo: '',
        gsm: '',
        password: '',
        firstName: '',
        lastName: '',
        email: '',
      });

      // Modal'ı kapat
      setShowAddModal(false);

      // Kullanıcı listesini yenile
      loadPage();
    } catch (error) {
      toast.error(error || 'Kullanıcı oluşturulamadı');
    }
  };

  // Kullanıcı silme fonksiyonu
  const handleDeleteUser = async (user) => {
    if (!user) return;
    if (!canManageCustomers) {
      toast.error(PERMISSION_DENIED_MESSAGE);
      return;
    }

    setIsDeleting(true);
    try { 
      await dispatch(deleteUser(user.tc)).unwrap();
      
      setDeleteConfirm({ show: false, user: null });
      
      toast.success('✅ Kullanıcı başarıyla silindi!', { 
        id: 'delete-user',
        duration: 4000,
        icon: '🗑️'
      });
      
      // Reducer satırı hemen kaldırır; sayfa ve toplam backend'den tazelenir
      loadPage();
    } catch (error) {
      toast.error(error || 'Kullanıcı silinirken bir hata oluştu', { 
        id: 'delete-user',
        duration: 4000
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <ModalReporter open={showAddModal || deleteConfirm.show} />
      <div className="space-y-4">
        {/* Header */}
      <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-lg font-bold text-gray-900 tracking-tight">Müşteri Yönetimi</h1>
          <p className="text-gray-500 mt-2 text-lg">Tüm kullanıcı hesaplarını ve detaylarını yönetin</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => {
              if (!canManageCustomers) {
                toast.error(PERMISSION_DENIED_MESSAGE);
                return;
              }
              setShowAddModal(true);
            }}
            className={`flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-2xl transition-all shadow-lg shadow-blue-200 font-semibold ${permissionGuardClass(canManageCustomers)} ${canManageCustomers ? 'hover:bg-blue-700' : ''}`}
            aria-disabled={!canManageCustomers}
          >
            <Plus size={20} /> Yeni Kullanıcı
          </button>
          <button
            onClick={() => {
              if (!canManageCustomers) {
                toast.error(PERMISSION_DENIED_MESSAGE);
              }
            }}
            className={`flex items-center gap-2 px-6 py-3 bg-white text-gray-700 border border-gray-200 rounded-2xl transition-all font-medium ${permissionGuardClass(canManageCustomers)} ${canManageCustomers ? 'hover:bg-gray-50' : ''}`}
            aria-disabled={!canManageCustomers}
          >
            <Upload size={20} /> İçe Aktar
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="TC, isim, e-posta veya telefon ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-transparent focus:bg-white border focus:border-blue-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-50/50 transition-all font-medium"
            />
          </div>

          <div
            className={`flex gap-4 overflow-x-auto pb-2 lg:pb-0 ${isSearching ? 'opacity-50 pointer-events-none' : ''}`}
            title={isSearching ? 'Arama tüm müşterilerde yapılıyor; tarih filtresi aramayı silince uygulanır' : undefined}
            aria-disabled={isSearching}
          >
            <Dropdown
              items={[
                { value: 'all', label: 'Tüm Zamanlar' },
                { value: 'today', label: 'Bugün' },
                { value: 'week', label: 'Son 7 gün' },
                { value: 'month', label: 'Son 30 gün' },
                { value: 'year', label: 'Son 365 gün' },
              ]}
              value={dateFilter}
              onChange={handleDateFilterChange}
              simple
              className="min-w-[200px]"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] table-fixed">
            <colgroup>
              <col className="w-[24%]" />
              <col className="w-[28%]" />
              <col className="w-[14%]" />
              <col className="w-[10%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
            </colgroup>
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100">
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Kullanıcı</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">İletişim</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Kayıt Tarihi</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Puan</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Segment</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-gray-500 font-medium">Yükleniyor...</p>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="6" className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-red-500">
                        <XCircle size={24} />
                      </div>
                      <p className="text-red-600 font-medium">
                        {typeof error === 'string' ? error : error?.message || 'Bir hata oluştu'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center text-gray-400">
                        <Search size={32} />
                      </div>
                      <p className="text-gray-500 font-medium text-lg">
                        {hasActiveFilters
                          ? 'Arama kriterlerine uygun kullanıcı bulunamadı'
                          : 'Henüz kullanıcı bulunmuyor'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : users.map((user) => (
                <tr key={user.id} className={`group hover:bg-gray-50/80 transition-all duration-200 ${loading ? 'opacity-50' : ''}`}>
                  <td className="px-6 py-4 align-middle">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-600 flex items-center justify-center font-bold text-sm">
                        {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 truncate">{user.name || 'İsimsiz'}</div>
                        <div className="text-sm text-gray-500 font-mono truncate">{user.tc}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 align-middle">
                    <div className="space-y-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm text-gray-600 min-w-0">
                        <Mail size={14} className="text-gray-400 shrink-0" />
                        <span className="truncate">{user.email || '-'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 min-w-0">
                        <Phone size={14} className="text-gray-400 shrink-0" />
                        <span className="truncate">{user.phone || '-'}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 align-middle text-sm text-gray-600 font-medium whitespace-nowrap">
                    {user.registerDate || '-'}
                  </td>
                  <td className="px-6 py-4 align-middle whitespace-nowrap">
                    <span className="font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-lg">
                      {user.loyaltyPoints ?? '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 align-middle whitespace-nowrap">
                    {user.segment ? (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${user.segment.toLowerCase() === 'gold' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        user.segment.toLowerCase() === 'silver' ? 'bg-slate-50 text-slate-700 border-slate-200' :
                          'bg-orange-50 text-orange-800 border-orange-200'
                        }`}>
                        {user.segment}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 align-middle text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/kullanicilar/${user.id}`)}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-xl transition-colors"
                        title="Detayları Görüntüle"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => {
                          if (!canManageCustomers) {
                            toast.error(PERMISSION_DENIED_MESSAGE);
                            return;
                          }
                          setDeleteConfirm({ show: true, user });
                        }}
                        className={`p-2 text-red-600 rounded-xl transition-colors ${permissionGuardClass(canManageCustomers)} ${canManageCustomers ? 'hover:bg-red-100' : ''}`}
                        title="Kullanıcıyı Sil"
                        aria-disabled={!canManageCustomers}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-8 py-4 border-t border-gray-100 bg-gray-50/30">
          <p className="text-sm text-gray-500 font-medium">
            Toplam <span className="font-bold text-gray-900">{totalUsers.toLocaleString('tr-TR')}</span> kayıt
          </p>
          <NumberedPagination
            currentPage={currentPage}
            totalPages={totalPages}
            paginationItemsToDisplay={5}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && createPortal((
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-3 animate-in fade-in duration-200">
          <div className="bg-white rounded-xl p-4 max-w-2xl w-full max-h-[88vh] overflow-y-auto shadow-2xl scale-100 animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-5">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Yeni Kullanıcı Ekle</h2>
                <p className="text-gray-500 mt-1">Yeni bir müşteri kaydı oluşturun</p>
              </div>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Zorunlu Alanlar */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    TC Kimlik No <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.tcNo}
                    onChange={(e) => setFormData({ ...formData, tcNo: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-medium"
                    placeholder="12345678901"
                    maxLength={11}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Telefon <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.gsm}
                    onChange={(e) => setFormData({ ...formData, gsm: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-medium"
                    placeholder="05321112233"
                    maxLength={11}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Şifre <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-medium"
                  placeholder="En az 6 karakter"
                  minLength={6}
                />
              </div>

              {/* Opsiyonel Alanlar */}
              <div className="bg-gray-50 rounded-2xl p-4 space-y-4">
                <p className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  <span className="w-1 h-4 bg-blue-500 rounded-full"></span>
                  Kişisel Bilgiler (Opsiyonel)
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ad</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      placeholder="Ahmet"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Soyad</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      placeholder="Yılmaz"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">E-posta</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                    placeholder="ornek@email.com"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4 mt-5 pt-6 border-t border-gray-100">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setFormData({
                    tcNo: '',
                    gsm: '',
                    password: '',
                    firstName: '',
                    lastName: '',
                    email: '',
                  });
                }}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-bold text-gray-600"
              >
                İptal
              </button>
              <button
                onClick={handleCreateUser}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-bold shadow-lg shadow-blue-200"
              >
                Kullanıcı Oluştur
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && createPortal((
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-xl p-4 max-w-xl w-full mx-4 shadow-2xl">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-500">
              <Trash2 size={32} />
            </div>

            <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">Kullanıcıyı Sil</h3>
            <p className="text-gray-500 mb-5 text-center">
              <span className="font-semibold text-gray-900">{deleteConfirm.user?.name}</span> adlı kullanıcıyı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </p>

            <div className="bg-gray-50 rounded-2xl p-4 mb-5 border border-gray-100">
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">TC No:</span>
                  <span className="font-medium text-gray-900">{deleteConfirm.user?.tc}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">E-posta:</span>
                  <span className="font-medium text-gray-900">{deleteConfirm.user?.email}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Telefon:</span>
                  <span className="font-medium text-gray-900">{deleteConfirm.user?.phone}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setDeleteConfirm({ show: false, user: null })}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-bold text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                İptal
              </button>
              <button
                onClick={() => handleDeleteUser(deleteConfirm.user)}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors font-bold shadow-lg shadow-red-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Siliniyor...
                  </>
                ) : (
                  'Evet, Sil'
                )}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
      </div>
    </>
  );
};

export default UserList;

